import { useState, type FC, useEffect, Suspense } from 'react';

import { ErrorBoundary, Grid } from '@packages/shared';
import type { GTMEventGlycerinPagination } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

import { ProductGrid } from '../ProductGrid';
import type { ProductGridProps } from '../ProductGrid';
import { usePaging } from '../../hooks/usePaging';
import { ProductGridLoaderSwitch } from './ProductGridLoaderSwitch';
import { ProductGridSkeleton } from '../ProductGridSkeleton';
import { useDisplayMode } from '../../hooks/useDisplayMode';

export const InfiniteProductGridFragment = graphql(/* GraphQL */ `
  fragment InfiniteProductGridFragment on SearchProductResult {
    ...ProductGridLoaderSwitchFragment
    ...ProductGridFragment
    ...UsePagingFragment
    ...UseDisplayModeFragment
  }
`);

export type InfiniteProductGridProps = Pick<ProductGridProps, 'topLevelCategories'> & {
  maskedResult: FragmentType<typeof InfiniteProductGridFragment>;
  referrer?: string;
  /**
   * How many products are loaded at once on-demand when scrolling down
   *
   * @default 72 */
  dynamicPageSize?: number;
  ProductGridComponent?: FC<ProductGridProps>;
};

export const InfiniteProductGrid: FC<InfiniteProductGridProps> = (props) => {
  const {
    maskedResult,
    referrer,
    dynamicPageSize = 72,
    ProductGridComponent = ProductGrid,
    topLevelCategories,
  } = props;

  const result = getFragmentData(InfiniteProductGridFragment, maskedResult);
  const { pageSize: initialPageSize } = usePaging(result);
  const { displayMode } = useDisplayMode(result);

  const [pagesToDisplay, setPagesToDisplay] = useState(1);

  const dispatchGtmEvent = useTracking();

  useEffect(() => {
    // initial page is not tracked because it is not a pagination interaction
    if (pagesToDisplay <= 1) return;

    dispatchGtmEvent<GTMEventGlycerinPagination>({
      event: 'Pagination',
      PaginationData: {
        pageNumber: pagesToDisplay,
      },
    });
  }, [dispatchGtmEvent, pagesToDisplay]);

  // this may have some drawbacks in terms of referential identity when it comes to re-renders, but an embedded arrow function in the JSX has the same issues
  // unfortunately since the callbacks are index-specific, they cannot be memoized properly
  const maybeShowNextPageAfter =
    (pageIndex: number, pageSize: number) =>
    (cardIndexInPage = 0) => {
      // load next page when 70% of the current page is visible
      if (cardIndexInPage + 1 >= 0.7 * pageSize) {
        setPagesToDisplay((previous) => {
          // page index is 0-based, +1 gives corresponding 1-based page number, +2 gives next page number
          const nextPageNumber = pageIndex + 2;

          // only change the number of pages to display if the next page number is greater than the current number of pages
          // otherwise it would be possible to hide pages again due to race conditions
          // shown pages should always be monotonically increasing
          return Math.max(previous, nextPageNumber);
        });
      }
    };

  return (
    <>
      {Array.from({ length: pagesToDisplay }).map((_, pageIndex) => (
        // NOTE: there is no other possible key for the page than the page index
        // eslint-disable-next-line react/no-array-index-key
        <Grid key={`page-${pageIndex}`} container>
          <Grid sx={{ marginTop: 2.5 }} item>
            {pageIndex === 0 ? (
              <ProductGridComponent
                maskedResult={result}
                onProductFirstBecameVisible={maybeShowNextPageAfter(pageIndex, initialPageSize)}
                pageNumberForTracking={pageIndex + 1}
                topLevelCategories={topLevelCategories}
              />
            ) : (
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <ProductGridSkeleton
                      displayMode={displayMode ?? 'cards'}
                      count={dynamicPageSize}
                    />
                  }
                >
                  <ProductGridLoaderSwitch
                    limit={dynamicPageSize}
                    offset={initialPageSize + pageIndex * dynamicPageSize}
                    referrer={referrer}
                    ProductGridComponent={ProductGridComponent}
                    maskedResult={result}
                    // ProductGridProps
                    onProductFirstBecameVisible={maybeShowNextPageAfter(pageIndex, dynamicPageSize)}
                    pageNumberForTracking={pageIndex + 1}
                    topLevelCategories={topLevelCategories}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </Grid>
        </Grid>
      ))}
    </>
  );
};
